import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchPharmaceuticalForm, generateNovaReport, downloadNovaReport } from '../services/analyticsService';
import MultiSelectDropdown from '../components/shared/MultiSelectDropdown';
import { NovaQuickAnalysisBar } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const CHART_CARD = { background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '20px 16px', display: 'flex', flexDirection: 'column' };
const COLORS = ['#0D5C4F','#0B3D6B','#3B1F6E','#8B4513','#0F6B5B','#1466A7','#5C318C','#AB611F','#18A78F','#1E8FE0','#7D43AA','#E49937'];

function NovaInsight({ text }) {
  if (!text) return null;
  return (
    <div style={{ ...CHART_CARD, padding: '14px 18px', marginBottom: 20, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
      <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

export default function PharmaceuticalFormDevelopments() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [formFilter, setFormFilter] = useState('');
  const [selectedForms, setSelectedForms] = useState(() => {
    const p = new URLSearchParams(location.search);
    const f = p.get('form');
    return f ? new Set([f]) : new Set();
  });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  const handleExport = useCallback(async (format) => {
    const setLd = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLd(true); setExportErr('');
    try {
      const forms = selectedForms.size > 0 ? Array.from(selectedForms) : [];
      const filters = forms.length > 0 ? { forms } : undefined;
      const meta = await generateNovaReport(msalInstance, { reportType: 'pharmaceutical-form', format, filters, language: reportLang });
      const blob = await downloadNovaReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `pharmaceutical-form-report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLd(false); }
  }, [msalInstance, selectedForms, reportLang]);

  const load = useCallback(() => {
    setLoading(true); setErr('');
    const params = {};
    if (formFilter.trim()) params.form = formFilter.trim();
    fetchPharmaceuticalForm(msalInstance, params)
      .then(d => {
        setData(d);
        if (d.leaderboard && selectedForms.size === 0) {
          setSelectedForms(new Set(d.leaderboard.slice(0, 6).map(l => l.dimension)));
        }
      })
      .catch(() => setErr('Failed to load pharmaceutical form data'))
      .finally(() => setLoading(false));
  }, [formFilter, msalInstance]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const allForms = useMemo(() => {
    if (!data?.leaderboard) return [];
    return data.leaderboard.map(l => l.dimension);
  }, [data]);

  const topForms = useMemo(() => {
    if (!data?.leaderboard) return [];
    return data.leaderboard.slice(0, 8).map(l => l.dimension);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data?.mixOverTime || topForms.length === 0) return [];
    return data.mixOverTime.map(pt => {
      const entry = { quarter: pt.quarter };
      for (const f of topForms) entry[f] = pt[f] || 0;
      return entry;
    });
  }, [data, topForms]);

  // Sync URL ↔ selectedForms
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (selectedForms.size === 1) {
      const [only] = selectedForms;
      if (p.get('form') !== only) navigate(`?form=${encodeURIComponent(only)}`, { replace: true });
    } else if (p.has('form')) {
      navigate(location.pathname, { replace: true });
    }
  }, [selectedForms, location.search, location.pathname, navigate]);

  const scopeToForm = useCallback((form) => {
    setSelectedForms(new Set([form]));
  }, []);

  const toggleForm = (f) => {
    setSelectedForms(prev => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f); else next.add(f);
      return next;
    });
  };

  const btnBase = {
    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
  };

  return (
    <div className="sup-page">
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Pharmaceutical form developments</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
          <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
          <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
            ...btnBase, cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
          }}>
            {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
          </button>
          <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
            ...btnBase, cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
          }}>
            {exportingXlsx ? 'Generating...' : 'Download Excel'}
          </button>
        </div>
      </div>

      {data?.summary && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
            <div className="sup-kpi-lbl">Forms</div><div className="sup-kpi-val">{data.summary.totalForms}</div>
          </div>
          <div className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
            <div className="sup-kpi-lbl">Quarters</div><div className="sup-kpi-val">{data.summary.totalQuarters}</div>
          </div>
        </div>
      )}

      <NovaQuickAnalysisBar
        placeholder="Quick analysis — type a dosage form..."
        knownEntities={allForms}
        entityLabel="dosage form"
        reportType="pharmaceutical-form"
        filterBuilder={(entity) => ({ forms: [entity] })}
        onSubmit={(entity) => { setSelectedForms(new Set([entity])); setFormFilter(''); }}
      />

      <NovaInsight text={data?.novaSummary} />

      {/* Multi-select dropdown filter */}
      {allForms.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4, fontSize: 11, color: 'var(--t2)', fontWeight: 500 }}>
            Filter dosage forms {selectedForms.size > 0 && <span style={{ color: 'var(--t3)' }}>({selectedForms.size} selected)</span>}
          </div>
          <MultiSelectDropdown
            options={allForms}
            selected={selectedForms}
            onToggle={toggleForm}
            onClear={() => setSelectedForms(new Set())}
            onSelectAll={() => setSelectedForms(new Set(allForms))}
            placeholder="Filter forms..."
            searchPlaceholder="Search forms..."
          />
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}
      {err && <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}><p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p></div>}

      {!loading && !err && data && (
        <>
          {/* Stacked area chart — form mix over time */}
          {chartData.length > 0 && topForms.length > 0 && (
            <div style={{ ...CHART_CARD, marginBottom: 20 }}>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Form Mix Over Time (% of New Products, Top {topForms.length})</div>
              <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData} stackOffset="expand" margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#6c757d' }} interval={Math.max(0, Math.floor(chartData.length / 12))} />
                    <YAxis tickFormatter={v => `${Math.round(v * 100)}%`} tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                    <Tooltip formatter={(v) => `${typeof v === 'number' ? v.toFixed(1) : v}%`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {topForms.map((f, i) => (
                      <Area key={f} type="monotone" dataKey={f} stackId="1" stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.6} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="sup-table-wrap">
            <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              Emerging Formats (Recent 2 Quarters)
            </div>
            <div className="sup-table-scroll">
              <table className="sup-table">
                <thead><tr><th>Form</th><th>Recent Growth</th><th>Prior Growth</th><th>Cumulative</th></tr></thead>
                <tbody>
                  {(data.leaderboard || []).map((r, i) => (
                    <tr key={i} role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                      onClick={() => scopeToForm(r.dimension)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scopeToForm(r.dimension); } }}
                    >
                      <td style={{ fontWeight: 600 }}>{r.dimension}</td>
                      <td><span className="sup-badge teal-soft">+{r.recentGrowth}</span></td>
                      <td>{r.priorGrowth}</td>
                      <td><strong>{r.cumulative}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Based on recorded product history.</div>
        </>
      )}
    </div>
  );
}
